"use client";

import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import * as React from "react";

import { cn } from "@/lib/utils";

const TooltipProvider = TooltipPrimitive.Provider;

const Tooltip = TooltipPrimitive.Root;

const TooltipTrigger = TooltipPrimitive.Trigger;

const TooltipPortal = TooltipPrimitive.Portal;

const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(({ className, sideOffset = 8, ...props }, ref) => (
  <TooltipPortal>
    <TooltipPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      className={cn(
        "z-50 overflow-hidden rounded-2xl border-0 bg-[var(--primary-container)] px-4 py-3 text-sm text-[var(--primary-container-foreground)] shadow-lg elevation-3",
        "animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95",
        "data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
        "max-w-xs sm:max-w-sm",
        className
      )}
      {...props}
    />
  </TooltipPortal>
));
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

// Wrapper component that handles both hover and click
export function ClickableTooltip({ 
  children, 
  content,
  ...props 
}: {
  children: React.ReactElement;
  content: React.ReactNode;
} & React.ComponentPropsWithoutRef<typeof Tooltip>) {
  const [open, setOpen] = React.useState(false);

  // Clone the child element to add click handler
  const trigger = React.cloneElement(children, {
    onClick: (e: React.MouseEvent) => {
      // Toggle tooltip on click/tap - single tap to open/close
      e.stopPropagation();
      setOpen((prev) => !prev);
      // Call original onClick if it exists
      children.props.onClick?.(e);
    },
  });

  return (
    <Tooltip
      open={open}
      delayDuration={0}
      onOpenChange={(newOpen) => {
        // Always allow controlled state changes
        setOpen(newOpen);
      }}
      {...props}
    >
      <TooltipTrigger asChild>
        {trigger}
      </TooltipTrigger>
      <TooltipContent
        side="top"
        align="center"
        sideOffset={8}
        onPointerDownOutside={() => {
          setOpen(false);
        }}
        onEscapeKeyDown={() => {
          setOpen(false);
        }}
      >
        {content}
      </TooltipContent>
    </Tooltip>
  );
}

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider };
